import React from 'react';
import { CARD_STYLES } from 'constants/styles';

const MEAL_IMAGE_DIR = `${process.env.PUBLIC_URL || ''}/images/subscription`;
const INK = '#1a1a1a';
const MUTED = '#8a8580';

/** Image for a meal-type key ("breakfast" | "lunch" | "dinner" | "snack"). */
export const subscriptionMealImage = (key) => `${MEAL_IMAGE_DIR}/subscription-${key}.png`;

const stepBtn = (disabled) => ({
  width: '36px', height: '36px', padding: 0, fontFamily: 'inherit', touchAction: 'manipulation', borderRadius: '8px', border: '1px solid #dcd8d0', background: '#fff',
  color: INK, fontSize: '16px', lineHeight: 1, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
});

/**
 * One meal type's weekly quantity card, shared by the subscription builder and
 * the account panel's subscription editor. Border and background follow
 * CompactMealCard's CARD_STYLES (dashed when 0, orange once added).
 * @param {number} price per meal (or per day for snacks), already discounted
 * @param {string} unit shown after the price, e.g. "ea · 10% off"
 * @param {React.ReactNode} children rendered under the macros (the protein choice)
 */
export default function SubscriptionMealCard({ label, image, price, unit, note, nutrition, qty, onChange, max = 50, children }) {
  const dishCard = CARD_STYLES.container({ quantity: qty });
  return (
    <div style={{
      border: dishCard.border, backgroundColor: dishCard.backgroundColor, borderRadius: '16px',
      padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0, boxSizing: 'border-box', overflowWrap: 'anywhere',
    }}>
      {image && (
        <img src={image} alt={label} loading="lazy" style={{
          width: '100%', aspectRatio: '1', objectFit: 'contain', borderRadius: '12px', background: '#e9e6df', display: 'block',
        }} />
      )}

      {/* Wraps the stepper under the name when a narrow card can't fit the unbroken price line beside it. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: INK, lineHeight: 1.3 }}>{label}</div>
          <div style={{ fontSize: '14px', color: MUTED, marginTop: '4px', whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 700, color: '#333' }}>${Number(price || 0).toFixed(2)}</span> <span style={{ fontSize: '12px' }}>{unit}</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button type="button" aria-label={`Decrease ${label}`} onClick={() => onChange(Math.max(0, qty - 1))} disabled={qty <= 0} style={stepBtn(qty <= 0)}>−</button>
          <span style={{ minWidth: '24px', textAlign: 'center', fontSize: '16px', color: INK }}>{qty}</span>
          <button type="button" aria-label={`Increase ${label}`} onClick={() => onChange(Math.min(max, qty + 1))} disabled={qty >= max} style={stepBtn(qty >= max)}>+</button>
          {note && <span style={{ fontSize: '12px', color: MUTED, whiteSpace: 'nowrap' }}>{note}</span>}
        </div>
      </div>

      {nutrition && (
        <div>
          <div style={{ fontSize: '13px', fontWeight: 500, color: '#4a4744' }}>{Math.round(nutrition.calories)} kcal</div>
          <div style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
            {/* Each macro stays whole when the line wraps on a narrow card. */}
            {[`${Math.round(nutrition.carbsG)}g carbs`, `${Math.round(nutrition.proteinG)}g protein`, `${Math.round(nutrition.fatG)}g fat`, `${Math.round(nutrition.fiberG)}g fiber`]
              .map((m, i) => <React.Fragment key={m}>{i > 0 && ' · '}<span style={{ whiteSpace: 'nowrap' }}>{m}</span></React.Fragment>)}
          </div>
        </div>
      )}

      {children}
    </div>
  );
}
